import React, { useState, useEffect, useCallback } from 'react';
import {
  Megaphone, Plus, Edit2, Trash2, Search, Pin, PinOff, Info, AlertTriangle,
  AlertOctagon, Eye, EyeOff, Send, Calendar, Users, Building2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_CFG = {
  info:      { icon: Info,          color: 'bg-blue-50 text-blue-700 border-blue-200',       dot: 'bg-blue-500',       label: 'Info' },
  important: { icon: AlertTriangle, color: 'bg-amber-50 text-amber-800 border-amber-200',    dot: 'bg-amber-500',      label: 'Important' },
  urgent:    { icon: AlertOctagon,  color: 'bg-red-50 text-red-700 border-red-200',          dot: 'bg-red-500',        label: 'Urgent' },
};

const NoticeBoard = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState({ priorities: [], audiences: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [previewRecipients, setPreviewRecipients] = useState(null);

  const h = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = [
        propertyFilter && `property_id=${propertyFilter}`,
        priorityFilter && `priority=${priorityFilter}`,
        statusFilter && `status=${statusFilter}`,
      ].filter(Boolean).join('&');
      const [aRes, pRes, sRes, mRes] = await Promise.all([
        axios.get(`${API}/announcements${qs ? '?' + qs : ''}`, h),
        axios.get(`${API}/properties`, h),
        axios.get(`${API}/announcements/summary`, h),
        axios.get(`${API}/announcements/meta`, h),
      ]);
      setItems(aRes.data);
      setProperties(pRes.data);
      setSummary(sRes.data);
      setMeta(mRes.data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load notices', variant: 'destructive' });
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, propertyFilter, priorityFilter, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/announcements/${deleting.id}`, h);
      toast({ title: 'Notice deleted' });
      setDeleting(null); fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' });
    } finally { setDeleteLoading(false); }
  };

  const togglePin = async (a) => {
    try {
      await axios.post(`${API}/announcements/${a.id}/toggle-pin`, {}, h);
      fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const togglePublish = async (a) => {
    try {
      await axios.post(`${API}/announcements/${a.id}/toggle-publish`, {}, h);
      fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const openRecipientsPreview = async (a) => {
    try {
      const r = await axios.get(`${API}/announcements/${a.id}/recipients`, h);
      setPreviewRecipients({ announcement: a, ...r.data });
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const filtered = items.filter(a => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (a.title || '').toLowerCase().includes(s)
      || (a.body || '').toLowerCase().includes(s)
      || (a.property_name || '').toLowerCase().includes(s);
  });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="notice-board-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone className="text-purple-600" /> Notice Board
          </h1>
          <p className="text-gray-600 mt-1">Post announcements, rules, and updates for your tenants and team.</p>
        </div>
        <Button
          data-testid="add-notice-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus size={16} className="mr-2" /> New Notice
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Card className="p-4 border-l-4 border-l-purple-500">
            <p className="text-xs text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{summary.total}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-green-500">
            <p className="text-xs text-gray-500">Active</p>
            <p className="text-2xl font-bold text-green-700 mt-0.5">{summary.active}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-blue-500">
            <p className="text-xs text-gray-500">Pinned</p>
            <p className="text-2xl font-bold text-blue-700 mt-0.5">{summary.pinned}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-red-500">
            <p className="text-xs text-gray-500">Urgent Active</p>
            <p className="text-2xl font-bold text-red-700 mt-0.5">{summary.urgent}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-gray-400">
            <p className="text-xs text-gray-500">Drafts</p>
            <p className="text-2xl font-bold text-gray-700 mt-0.5">{summary.drafts}</p>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="notice-search"
            type="text"
            placeholder="Search title, body, property..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <select data-testid="notice-property-filter" value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select data-testid="notice-priority-filter" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Priorities</option>
          {meta.priorities.map(p => <option key={p} value={p}>{PRIORITY_CFG[p]?.label || p}</option>)}
        </select>
        <select data-testid="notice-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center" data-testid="notices-empty">
          <Megaphone size={56} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-xl font-semibold mb-2">No notices yet</h3>
          <p className="text-gray-600 mb-4">Keep your tenants and team in the loop — post your first announcement.</p>
          <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-purple-600 hover:bg-purple-700">
            <Plus size={16} className="mr-2" /> Create First Notice
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(a => {
            const cfg = PRIORITY_CFG[a.priority] || PRIORITY_CFG.info;
            const Icon = cfg.icon;
            return (
              <Card
                key={a.id}
                data-testid={`notice-card-${a.id}`}
                className={`relative overflow-hidden border-l-4 transition hover:shadow-md ${
                  a.priority === 'urgent' ? 'border-l-red-500' :
                  a.priority === 'important' ? 'border-l-amber-500' : 'border-l-blue-500'
                } ${!a.is_active ? 'opacity-60' : ''}`}
              >
                {a.pinned && (
                  <div className="absolute top-2 right-2 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                    <Pin size={10} /> Pinned
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${cfg.color} border`}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{a.title}</h3>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 flex-wrap">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${cfg.color} border`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
                          {cfg.label}
                        </span>
                        <span className="inline-flex items-center gap-1"><Building2 size={10} /> {a.property_name}</span>
                        <span className="inline-flex items-center gap-1"><Users size={10} /> {a.audience}</span>
                        {a.expires_at && (
                          <span className="inline-flex items-center gap-1"><Calendar size={10} /> expires {a.expires_at}</span>
                        )}
                        {!a.published && <span className="bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-xs">Draft</span>}
                        {a.published && !a.is_active && <span className="bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded text-xs">Expired</span>}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-line line-clamp-4">{a.body}</p>
                  <div className="flex items-center gap-1 mt-4 pt-3 border-t flex-wrap">
                    <button
                      data-testid={`notice-pin-${a.id}`}
                      onClick={() => togglePin(a)}
                      className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded flex items-center gap-1"
                      title={a.pinned ? 'Unpin' : 'Pin to top'}
                    >
                      {a.pinned ? <PinOff size={12} /> : <Pin size={12} />}
                      {a.pinned ? 'Unpin' : 'Pin'}
                    </button>
                    <button
                      data-testid={`notice-publish-${a.id}`}
                      onClick={() => togglePublish(a)}
                      className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded flex items-center gap-1"
                      title={a.published ? 'Unpublish' : 'Publish'}
                    >
                      {a.published ? <EyeOff size={12} /> : <Eye size={12} />}
                      {a.published ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                      data-testid={`notice-recipients-${a.id}`}
                      onClick={() => openRecipientsPreview(a)}
                      className="px-2 py-1 text-xs text-purple-600 hover:bg-purple-50 rounded flex items-center gap-1"
                      title="Preview recipients"
                    >
                      <Send size={12} /> Recipients
                    </button>
                    <div className="ml-auto flex items-center gap-1">
                      <button
                        data-testid={`notice-edit-${a.id}`}
                        onClick={() => { setEditing(a); setShowForm(true); }}
                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit notice"
                      ><Edit2 size={14} /></button>
                      <button
                        data-testid={`notice-delete-${a.id}`}
                        onClick={() => setDeleting(a)}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete notice"
                      ><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <NoticeFormModal
          open={showForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSuccess={fetchData}
          editing={editing}
          properties={properties}
          meta={meta}
          token={token}
        />
      )}

      {previewRecipients && (
        <RecipientsModal
          data={previewRecipients}
          onClose={() => setPreviewRecipients(null)}
        />
      )}

      <DeleteConfirmationModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete Notice"
        message="Are you sure? This cannot be undone."
        itemName={deleting?.title || ''}
        loading={deleteLoading}
      />
    </div>
  );
};

// ---------- Notice Form ----------
const NoticeFormModal = ({ open, onClose, onSuccess, editing, properties, meta, token }) => {
  const [form, setForm] = useState({
    title: '', body: '', priority: 'info', audience: 'all',
    property_id: '', pinned: false, published: true, expires_at: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) setForm({
      title: editing.title || '',
      body: editing.body || '',
      priority: editing.priority || 'info',
      audience: editing.audience || 'all',
      property_id: editing.property_id || '',
      pinned: !!editing.pinned,
      published: editing.published !== false,
      expires_at: editing.expires_at || '',
    });
  }, [editing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        property_id: form.property_id || null,
        expires_at: form.expires_at || null,
      };
      if (editing) {
        await axios.put(`${API}/announcements/${editing.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Notice updated' });
      } else {
        await axios.post(`${API}/announcements`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Notice created' });
      }
      onSuccess(); onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{editing ? 'Edit Notice' : 'New Notice'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="notice-form">
          <div>
            <Label>Title *</Label>
            <Input data-testid="notice-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g., Water supply off tomorrow" required maxLength={120} />
          </div>
          <div>
            <Label>Message *</Label>
            <textarea
              data-testid="notice-body"
              value={form.body}
              onChange={e => setForm({ ...form, body: e.target.value })}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Write the full message here..."
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Priority</Label>
              <select data-testid="notice-priority" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                {meta.priorities.map(p => <option key={p} value={p}>{PRIORITY_CFG[p]?.label || p}</option>)}
              </select>
            </div>
            <div>
              <Label>Audience</Label>
              <select data-testid="notice-audience" value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                {meta.audiences.map(a => <option key={a} value={a} className="capitalize">{a}</option>)}
              </select>
            </div>
            <div>
              <Label>Property</Label>
              <select data-testid="notice-property" value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">All Properties</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Expires on (Optional)</Label>
              <Input data-testid="notice-expires" type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input data-testid="notice-pinned" type="checkbox" checked={form.pinned} onChange={e => setForm({ ...form, pinned: e.target.checked })} />
              Pin to top
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input data-testid="notice-published" type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} />
              Publish now
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <Button data-testid="notice-submit" type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={saving}>
              {saving ? 'Saving...' : (editing ? 'Update Notice' : 'Post Notice')}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Recipients preview ----------
const RecipientsModal = ({ data, onClose }) => {
  const { announcement, tenants = [], staff = [], total } = data;
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Recipients — {announcement.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
            This notice will reach <span className="font-bold text-purple-900">{total}</span> people
            ({tenants.length} tenants{staff.length ? ` + ${staff.length} staff` : ''}).
          </div>
          {tenants.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Tenants ({tenants.length})</h4>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {tenants.map(t => (
                  <div key={t.id} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                    <span className="font-medium text-gray-900">{t.name}</span>
                    <span className="text-xs text-gray-500">{t.phone}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {staff.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Staff ({staff.length})</h4>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {staff.map(s => (
                  <div key={s.id} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                    <div>
                      <span className="font-medium text-gray-900">{s.name}</span>
                      {s.role && <span className="text-xs text-gray-500 ml-2">· {s.role}</span>}
                    </div>
                    <span className="text-xs text-gray-500">{s.phone}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded p-2">
            💡 WhatsApp broadcast via Kolly will be enabled once Kolly is trained. For now, this is a preview of who will receive the notice.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NoticeBoard;
